// --- internal
import { useBrand } from "../brand";
import { useI18n, useSystem } from "../system";

// --- utils
import {
  useMoney,
  DetailedError,
  ErrorOrigin,
  responseCodes,
  useLaravalSchemaParser,
  useTranslateField,
  useTranslateName,
  useValidation,
  useImageUrl,
  parseFlattened
} from "../../utils";

import {
  compact,
  concat,
  filter,
  find,
  first,
  flatMap,
  forEach,
  get,
  has,
  isArray,
  isEmpty,
  isFunction,
  isNil,
  isNumber,
  keys,
  map,
  maxBy,
  merge,
  minBy,
  omitBy,
  orderBy,
  reduce,
  reverse,
  set,
  some,
  subtract,
  times,
  toNumber,
  trim,
  uniq,
  values
} from "lodash-es";

import type {
  IBasketProduct,
  IImage,
  IProduct,
  IProductAttribute,
  IProductPrice,
  IProductOption,
  IProductCategory
} from "@upmind-automation/types";
// --- types
import {
  BrandConfigKeys,
  DefaultPaymentPeriod,
  ProductTypes,
  PromotionDisplayTypes,
  ProvisionCategoryCodes,
  PriceDisplayTypes
} from "@upmind-automation/types";

import type {
  ExternalError,
  IProductConfig,
  PriceCalculations,
  PriceDetail,
  PriceDisplay,
  Product,
  ProductBundle,
  ProductBundles,
  ProductConfigContext,
  ProductDetails,
  ProductImage,
  TermDetails,
  ProductModel,
  ProductProps,
  SubproductModel,
  SubproductValue,
  PromotionDetails,
  SubproductDetails,
  ProductSummaryDetail,
  ProductSummaryDetailWithPrice,
  SubproductModelValue,
  UIMeta,
  ProductBreadcrumb
} from "./types";
import { UI_SCHEMA_DEFAULTS } from "./types";
import { ErrorObject } from "ajv";
import { BrandMeta } from "../brand/types";

// -----------------------------------------------------------------------------

/**
 * Computes the title for a product based on a template string derived from the product's UiMeta > uischema
 * The template string can contain placeholders in the form of {{key}} which will be replaced with the value of the key from the product
 * Templates may be nested through the product's category hierarchy, with the first template found being used
 * If no template is found or is empty, the fallback title is used
 *
 * @param product The product to compute the title for
 * @param basketProduct The basket product to use for translations
 * @param valueKey The key to extract values from at each level
 * @param fallback The fallback title to use if no template is found
 * @returns The computed title
 */
export function useUischemaTitle(
  product: IProduct,
  {
    basketProduct,
    valueKey,
    fallback
  }: {
    basketProduct?: IBasketProduct;
    valueKey: string;
    fallback: string;
  }
): string {
  const templates = compact(
    uniq(
      iterateParents(product.category, [get(product, valueKey)], {
        valueKey,
        parentKey: "top_category"
      })
    )
  ) as string[];

  if (isEmpty(templates)) return fallback;

  // ---
  const template = first(templates) ?? "";
  const result = template.replace(
    /{{([^{}]+)}}/g,
    (_keyExpr, key) =>
      useTranslateField(basketProduct, key) ??
      useTranslateField(product, key) ??
      ""
  );

  return isEmpty(result) ? fallback : result;
}

/**
 * Computes the name for a product based on the type of product and its translations
 * This allows us to make assumptions on the most appropriate name to use based on the product type
 * eg: for a domain product, we would use the service_identifier as the name where possible
 *
 * @param product The product to compute the name for
 * @param basketProduct The basket product to use for translations
 * @returns The computed name
 *
 */
export function useProductName(
  product: IProduct,
  basketProduct?: IBasketProduct
): string {
  const name = useTranslateName(product);

  if (!basketProduct?.service_identifier) return name;

  // individual product types may have different naming conventions
  switch (basketProduct?.product.provision_blueprint?.code) {
    case ProvisionCategoryCodes.DOMAIN_NAMES:
      return basketProduct?.service_identifier;

    case ProvisionCategoryCodes.SHARED_HOSTING:
    case ProvisionCategoryCodes.AUTO_LOGIN:
    case ProvisionCategoryCodes.SEO:
    case ProvisionCategoryCodes.WEBSITE_BUILDERS:
    case ProvisionCategoryCodes.SOFTWARE_LICENSES:
    case ProvisionCategoryCodes.SERVERS:
    default:
      return trim(`${name} (${basketProduct.service_identifier})`);
  }
}

/**
 * This function determines the price display type for a product based on a hierarchy of settings
 * 1. It first checks for a price display type set in the product's category hierarchy (meta.uischema.price_display_type)
 * 2. If not found, it checks for a product-specific override based on the product's provision blueprint code
 * 3. If still not found, it falls back to a brand-wide default price display type from the brand configuration
 * @param product
 * @param category
 * @returns
 */
function getPriceDisplayType(raw: IProduct): PriceDisplayTypes | undefined {
  const { getConfigValue } = useBrand();

  const brandDisplayType = getConfigValue<PriceDisplayTypes>(
    BrandConfigKeys.PRICE_DISPLAY_TYPE
  );

  const meta = parseFlattened(parseMeta(raw?.meta ?? {}, raw.category));
  const configDisplayType = get(meta, "uischema.price_display_type");
  // ----

  let productDisplayType = undefined;
  // individual product types may have different naming conventions
  switch (raw.provision_blueprint?.code) {
    case ProvisionCategoryCodes.DOMAIN_NAMES:
      productDisplayType ??= PriceDisplayTypes.CYCLE;
      break;

    case ProvisionCategoryCodes.SHARED_HOSTING:
    case ProvisionCategoryCodes.AUTO_LOGIN:
    case ProvisionCategoryCodes.SEO:
    case ProvisionCategoryCodes.WEBSITE_BUILDERS:
    case ProvisionCategoryCodes.SOFTWARE_LICENSES:
    case ProvisionCategoryCodes.SERVERS:
    default:
      productDisplayType ??= undefined;
      break;
  }

  return configDisplayType ?? productDisplayType ?? brandDisplayType;
}

/**
 * Recursively merges values from a property in a nested object hierarchy
 * @param item The current object in the hierarchy
 * @param result The array to collect values into
 * @param valueKey The key to extract values from at each level
 * @param parentKey The key to navigate to the parent object
 * @param initialValue Optional initial value to merge with collected values
 * @returns Merged values from all levels of the hierarchy, with lower levels and then initial value taking priority
 */
export function iterateParents(
  item: any,
  result: any[],
  {
    valueKey,
    parentKey,
    transform
  }: {
    valueKey: string;
    parentKey: string;
    transform?: (value: any) => any;
  }
): unknown[] {
  if (!item) return result;
  const parsed = isFunction(transform) ? transform(item) : get(item, valueKey);
  result.push(parsed);

  let parents = get(item, parentKey);
  if (isEmpty(parents)) return result;

  if (!isArray(parents)) parents = [parents];

  const nested = flatMap(parents, parent =>
    iterateParents(parent, [], {
      valueKey,
      parentKey,
      transform
    })
  );
  result.push(...nested);

  return result;
}

export function checkPriceOverride(
  values: SubproductModel,
  lookups: SubproductDetails[]
) {
  return some(values, (value, key) => {
    const item = find(lookups, ["id", key]);
    // make sure we only apply this IF this value is actually selected, ie has a value and is not empty
    return !isEmpty(value) && !!item?.meta?.overrides;
  });
}
// -----------------------------------------------------------------------------

export function checkQuantity(
  { lookups }: ProductConfigContext,
  value?: number
): string | undefined {
  const product = get(lookups, "product");
  const quantity = parseQuantity(value ?? 0, product);

  return !isNumber(value) || value < 1 || value !== quantity
    ? "Invalid Quantity Selected"
    : undefined;
}

export function checkTerm(
  { lookups }: ProductConfigContext,
  value?: number
): string | undefined {
  let term: TermDetails | undefined;

  term = find(lookups?.terms, ["cycle", value]);

  return isNil(term) ? "Valid Term is required" : undefined;
}

export function checkSubproducts(
  type: "attributes" | "options",
  { lookups }: ProductConfigContext,
  values?: ProductModel["attributes"] | ProductModel["options"]
): Record<string, string[]> | undefined {
  const errors: any = {};
  // ---
  // safety check, resolve if we have no attributes to check
  if (!lookups?.[type]?.length && isEmpty(values)) return undefined;

  forEach(lookups?.[type], (subproduct: SubproductDetails) => {
    let selected: Record<string, SubproductModelValue> = get(
      values,
      subproduct.id,
      {}
    );

    let error = [];

    // check if we are missing the required subproduct, if we are (and its not multiple) then automatically select the first one
    // NB we only do this on the initial load, not when we are updating the values
    if (isEmpty(selected) && subproduct?.meta.required) {
      error.push(`${subproduct.name} is required`);
    }

    // if we have selected values, ensure they are valid and fully formed

    // then parse each selected value and ensure it has all its required and VALID values
    forEach(selected, (value: SubproductModelValue) => {
      const product = find(subproduct.values, ["id", value.productId]);

      // safety check, ensure we have a valid product otherwise bail
      if (isEmpty(product))
        error.push(`${subproduct.name} is not a valid product`);
    });

    // check if we values too many values for this subproduct
    if (!subproduct?.meta.multiple && keys(selected)?.length > 1)
      error.push(`${subproduct.name} does not allow multiple choice`);

    if (!isEmpty(error)) errors[subproduct.id] = error;
  });

  return !isEmpty(errors) ? errors : undefined;
}

export function checkProvisioning(
  { lookups }: ProductConfigContext,
  values?: ProductModel["provisionFields"]
): ErrorObject[] {
  // this is our bypass for the validation when e are doing an express add
  // bail if we dont actually have any provision fields to check
  if (isEmpty(lookups?.provisionFields?.properties)) return [];
  const { validate } = useValidation();

  return lookups?.provisionFields
    ? validate(lookups.provisionFields, values)
    : [];
}

export const calculateBillingTerm = (
  period: DefaultPaymentPeriod | undefined,
  available: TermDetails[]
): TermDetails => {
  const { t } = useI18n();

  // because we have multiple options, we need to select one base don the following strategy:

  if (isEmpty(available))
    throw new DetailedError(
      t("error.terms_not_available"),
      responseCodes.Not_Found,
      ErrorOrigin.Headless
    );

  const { defaultPaymentPeriod } = useBrand();

  let term;

  switch (period) {
    case DefaultPaymentPeriod.HIGHEST_PRICE:
      term = maxBy(available, "price.currentAmount");
      break;
    case DefaultPaymentPeriod.LOWEST_PRICE:
      term = minBy(available, "price.currentAmount");
      break;
    case DefaultPaymentPeriod.LOWEST_MONTHLY_PRICE:
      term = minBy(available, "price.monthlyFromCurrentAmount");
      break;
    case DefaultPaymentPeriod.INHERIT_FROM_BRAND:
      term = calculateBillingTerm(defaultPaymentPeriod.value, available);
      break;

    default:
      break;
  }
  term ??= first(available);

  if (isEmpty(term))
    throw new DetailedError(
      t("error.terms_not_available"),
      responseCodes.Not_Found,
      ErrorOrigin.Headless
    );

  return term;
};

export function parseQuantity(
  quantity: number,
  product?: ProductDetails
): number {
  quantity = toNumber(quantity) || 1; // ensure we have a number;

  // Check the product data is available
  // Check the quantity is valid,
  //  - min Quantity matches the product min
  //  - max Quantity matches the product max
  //  - quantity is a multiple of the product step
  // ensure the quantity is at least the min, or 1

  if (quantity < Math.max(product?.min ?? 1, 1)) {
    quantity = Math.max(product?.min ?? 1, 1);
  }

  // ensure the quantity is at most the max (if set)
  if (product?.max && quantity > product?.max) {
    quantity = product?.max;
  }

  // ensure the quantity is a multiple of the step (if set)
  if (product?.step && quantity % product?.step !== 0) {
    quantity = Math.ceil(quantity / product.step) * product.step;
  }

  return quantity;
}

export function parseTerm(
  { lookups }: ProductConfigContext,
  value?: ProductModel["term"],
  quantity?: ProductModel["quantity"]
): { term: ProductModel["term"]; price: PriceCalculations["term"] } {
  let term: TermDetails | undefined = undefined;
  const price: number[] = [];
  // ---
  // try ge the full term object from the lookups terms
  term = find(lookups?.terms, ["cycle", value]);
  if (!term) {
    if (lookups?.terms?.length === 1) {
      term = first(lookups?.terms);
    } else {
      term = calculateBillingTerm(
        lookups?.product?.defaultPaymentPeriod,
        lookups?.terms ?? []
      );
    }
  }

  // set price values, taking into account the quantity and unit quantity
  // NB: we NEVER add, we always push into an array for the backend to handle
  times(quantity ?? 0, () => {
    price.push(term?.price?.currentAmount ?? 0);
  });

  return { term: get(term, "cycle") as number, price };
}

export function parseSubproducts(
  type: "attributes" | "options",
  { lookups, model, subproducts: subproductIds }: Partial<ProductConfigContext>,
  values: ProductModel["attributes"] | ProductModel["options"],
  quantity?: ProductModel["quantity"]
): {
  subproducts?: ProductModel["attributes"] | ProductModel["options"];
  price: PriceCalculations["attributes"] | PriceCalculations["options"];
} {
  let subproducts: SubproductModel = {};
  const price: any[] = [];
  // ---
  // safety check, resolve if we have no attributes to check
  if (!lookups?.[type]?.length) return { subproducts, price };

  const isInitial = isEmpty(model?.[type]);

  subproducts = reduce(
    lookups[type],
    (result, subproduct: SubproductDetails) => {
      let selected: Record<string, SubproductModelValue> = get(
        values,
        subproduct.id,
        {}
      );

      // try set anymatching pre-selected values for this subproduct ( subproductIds ),
      // NB: ONLY when values is being set for the first time
      if (isEmpty(values)) {
        forEach(subproductIds, pid => {
          if (some(subproduct.values, ["id", pid])) {
            set(selected, pid, { productId: pid });
          }
        });
      }

      // check if we are missing required subproduct, if we are (and its not multiple) then automaticaly select the first one
      // NB we only do this on the initial load, not when we are updating the values
      if (isEmpty(selected) && isInitial) {
        const defaultSubproduct = find(
          subproduct.values,
          "meta.default"
        ) as SubproductValue;

        if (defaultSubproduct) {
          set(selected, defaultSubproduct.id, {
            productId: defaultSubproduct.id
          });
        } else if (
          // TODO: simplyfy this to be just: we jsut need to update the ui errors before doing this
          //  subproduct?.meta.required && subproduct.values?.length === 1
          subproduct?.meta.required &&
          (!subproduct.meta.multiple ||
            (subproduct.meta.multiple && subproduct.values?.length === 1))
        ) {
          const pid = get(first(subproduct.values), "id");
          if (pid) set(selected, pid, { productId: pid });
        }
      }

      // if we have selected values, ensure they are valid and fully formed
      if (!isEmpty(selected)) {
        // only include valid values, stripping out any invalid ones, if we have any
        // selected = pickBy(selected, (_value, id) =>
        //   some(subproduct.values, ["id", id])
        // );

        // then parse each selected value, and ensure it has all its required and VALID values
        selected = reduce(
          selected,
          (
            result: Record<string, SubproductModelValue>,
            value: SubproductModelValue,
            id: string
          ) => {
            const product = find(subproduct.values, ["id", value.productId]);

            // safety check, ensure we have a valid product otherwise bail
            if (isEmpty(product)) return result;

            // Check if the product has pricing for the selected term
            // Do not filter out products with cycle 0 (one-time purchases)
            // NB: Only check products WITH pricing (aka options)
            if (
              !isEmpty(product.pricing) &&
              !some(product.pricing, ["cycle", model?.term]) &&
              !some(product.pricing, ["cycle", 0])
            )
              return result;

            // ensure we have a valid unit_quantity
            value.quantity = parseQuantity(Number(value.quantity), product);

            // ensure we map the product cycle to the value of the term ( if set ), but only if this is a recurring product
            // ie: if the cycle is 0, then we use the product cycle, otherwise we use the model term, fallbacking to the preferred product cycle
            value.cycle =
              product.cycle == 0
                ? product.cycle
                : (model?.term ?? product.cycle);

            set(result, id, value);

            // if we have a price, set price values, taking into account the quantity and unit quantity
            // NB: we NEVER add, we always push into an array for the backend to handle
            if (!isEmpty(product?.price)) {
              times(value.quantity * (quantity ?? 1), () => {
                price.push(product?.price?.currentAmount);
              });
            }

            // ---
            return result;
          },
          {}
        );
      }

      // ---
      set(result, subproduct.id, selected);

      return result;
    },
    {}
  );

  return { subproducts, price };
}

export const parseProductDetails = (
  rawProduct: IProduct,
  rawBasketProduct?: IBasketProduct
): ProductDetails => {
  return {
    id: rawProduct?.id,
    name: rawProduct.name,
    title: useUischemaTitle(rawProduct, {
      basketProduct: rawBasketProduct,
      valueKey: "meta.uischema.title",
      fallback: useProductName(rawProduct, rawBasketProduct)
    }),
    brand: useTranslateName(rawProduct?.brand),
    blueprintCode: rawProduct?.provision_blueprint?.code,
    categoryId: rawProduct?.category_id,
    category: useTranslateName(rawProduct?.category),
    categories: reverse(
      iterateParents(rawProduct.category, [], {
        valueKey: "name",
        parentKey: "top_category",
        transform: useTranslateName
      })
    ) as string[],
    breadcrumb: iterateParents(rawProduct.category, [], {
      valueKey: "name",
      parentKey: "top_category",
      transform: (category: IProductCategory) => {
        return {
          id: category.id,
          label: useTranslateName(category)
        } as ProductBreadcrumb;
      }
    }) as ProductBreadcrumb[],
    // ---
    cycle: rawProduct?.billing_cycle_months, // TODO check: cycle: rawProduct?.display_price_billing_cycle_months ?? rawProduct?.billing_cycle_months,
    defaultPaymentPeriod: rawProduct?.default_payment_period,
    displayPrice: find(parseTermDetails(rawProduct), [
      "cycle",
      rawProduct.display_price_billing_cycle_months
    ]),
    // ---
    description: useTranslateField(rawProduct, "description"),
    excerpt: useTranslateField(rawProduct, "short_description"),
    imgUrl: useImageUrl(rawProduct?.image?.full_url, "400x400"),
    images: parseProductImages(rawProduct?.images),
    // ---
    configurable:
      rawProduct.prices?.length > 1 ||
      !isEmpty(rawProduct.products_attributes) ||
      !isEmpty(rawProduct.products_options) ||
      !isEmpty(rawProduct.provision_fields),

    quantity: rawProduct?.min_order_quantity || rawProduct?.unit_quantity || 1,
    quantifiable: rawProduct?.order_type == 2,
    step: rawProduct?.unit_quantity || 1,
    min: rawProduct?.min_order_quantity || rawProduct?.unit_quantity || 1,
    max:
      rawProduct?.max_order_quantity > 0
        ? rawProduct?.max_order_quantity
        : Infinity,
    // ---
    uiMeta: parseMeta(
      parseFlattened(rawProduct?.meta ?? {}),
      parseFlattened(rawProduct?.category ?? {}) as IProductCategory,
      parseFlattened(rawProduct?.brand?.meta as BrandMeta)?.cart?.ui ?? {}
    ),
    uiCategoryMeta: rawProduct?.category?.meta || undefined
  };
};

export const parseMeta = (
  productMeta?: UIMeta,
  category?: IProductCategory,
  brandMeta?: UIMeta
): Record<string, any> => {
  productMeta ??= {};
  brandMeta ??= {};

  const categoryMeta = iterateParents(category, [], {
    valueKey: "meta",
    parentKey: "top_category"
  });

  // Priority order: brand (lowest) → categories → product (highest)
  // Start with brand meta, then merge each category meta, then product meta
  let result = merge({}, brandMeta);

  result = reduce(
    categoryMeta,
    (result, categoryMetaItem) => {
      return merge({}, result, categoryMetaItem);
    },
    result
  );

  // Product meta has highest priority, so merge it last
  result = merge({}, result, productMeta);

  // Apply defaults if no value is provided
  if (result.uischema) {
    result.uischema = merge({}, UI_SCHEMA_DEFAULTS, result.uischema);
  }

  return result;
};

export const parseTermDetails = (raw: IProduct): TermDetails[] => {
  const money = useMoney();
  const { uiCart } = useBrand();

  return map(orderBy(raw?.prices, "billing_cycle_months"), rawTerm => {
    const details: TermDetails = parseSummaryDetailWithPrice(rawTerm, raw);

    const trimTrailingZeroes =
      uiCart.value?.ui?.product?.display_price?.trim_trailing_zeroes ?? false;

    details.price.monthlyFromCurrentAmount =
      rawTerm.monthly_price_from_discounted ?? rawTerm.monthly_price_from;
    details.price.monthlyFromCurrentPrice =
      money.parsePrice(rawTerm.monthly_price_from_discounted_formatted, {
        trimTrailingZeroes
      }) ??
      money.parsePrice(rawTerm.monthly_price_from_formatted, {
        trimTrailingZeroes
      });
    details.price.monthlyFromRegularAmount = rawTerm.monthly_price_from;
    details.price.monthlyFromRegularPrice = money.parsePrice(
      rawTerm.monthly_price_from_formatted,
      { trimTrailingZeroes }
    );

    return details;
  });
};

export const parseSubproductDetails = (
  data?: (IProductAttribute | IProductOption)[],
  cycle?: number
): SubproductDetails[] => {
  const { includesTax } = useBrand();

  // safety check, bail if we have no data
  if (isEmpty(data)) return [];
  // When getting the attributes from the API, we get a flat list of attributes
  // We would rather have the attributes grouped by their category
  // And with each category having a list of attributes
  // so to do this we have to do the following:

  // 0. sort the data by attached_order for further lookups
  const sorted = orderBy(data, "attached_order");

  // then reduce the sorted data, creating a new object keyed by the category id
  // with the parsed data as the values
  const options: Record<string, SubproductDetails> = reduce(
    sorted,
    (result, rawSubproduct) => {
      // create the option based on the category ... if it isnt already set
      const option: SubproductDetails = get(result, rawSubproduct.category_id, {
        id: rawSubproduct.category.id,
        name: rawSubproduct.category.name,
        title: useTranslateName(rawSubproduct.category),
        description: useTranslateField(rawSubproduct.category, "description"),
        excerpt: useTranslateField(rawSubproduct.category, "short_description"),
        uiCategorymeta: rawSubproduct?.category.meta,
        uiMeta: parseMeta(
          parseFlattened(rawSubproduct?.meta ?? {}),
          parseFlattened(rawSubproduct?.category) as IProductCategory,
          parseFlattened(rawSubproduct?.brand?.meta as BrandMeta)?.cart?.ui
        ),
        uiCategoryMeta: rawSubproduct?.category?.meta || undefined,
        meta: {
          multiple: rawSubproduct.category.multiple,
          required: rawSubproduct.category.required,
          overrides: rawSubproduct.category.price_override
        }
      });

      // check EARLY if we have a price for one of the following:
      //  * no billing cycle set
      //  * a one off price
      //  * a matching billing cycle

      const valid =
        isNil(cycle) ||
        rawSubproduct.billing_cycle_months == 0 ||
        some(rawSubproduct.prices, ["billing_cycle_months", cycle]);

      // bail if the value is not valid, ie has no price that matches the current billing cycle
      if (!valid) return result;

      // get the prev values...if there are any
      const values: SubproductValue[] = get(option, "values", []);

      // ---
      const pricing: ProductSummaryDetailWithPrice[] = map(
        rawSubproduct.prices,
        rawPrice =>
          parseSummaryDetailWithPrice(
            rawPrice,
            rawSubproduct,
            rawSubproduct.category.price_override
          )
      );

      const price =
        find(pricing, ["cycle", 0]) || find(pricing, ["cycle", cycle]);

      const productDetails = parseProductDetails(rawSubproduct);

      const value: SubproductValue = {
        ...productDetails,
        cycle: price?.cycle ?? productDetails.cycle,
        price: price?.price,
        pricing: pricing,
        promotions: price?.promotions,
        meta: {
          // NB: only show term pricing if recurring!
          oneoff: rawSubproduct.billing_cycle_months == 0,
          discounted: !!price?.meta?.discounted,
          includesTax: includesTax.value,
          free: price?.price?.currentAmount == 0,
          overrides: rawSubproduct.category.price_override,
          default: !!rawSubproduct?.pivot?.default
        },
        order: rawSubproduct.pivot.order
      };
      // ---
      values.push(value as SubproductValue);
      set(option, "values", orderBy(values, "order"));

      // finally  set the updated option
      set(result, rawSubproduct.category_id, option);
      return result;
    },
    {}
  );

  // return just the values of the reduced object.
  return values(options);
};

export const parseSummaryDetail = (
  raw: IProductPrice,
  rawProduct: IProduct,
  overrides?: boolean
): ProductSummaryDetailWithPrice => {
  const { getBillingCycle } = useSystem();
  const { includesTax, getConfigValue } = useBrand();

  const cycle = getBillingCycle(raw.billing_cycle_months);

  const discounted =
    !isNil(raw.price_discounted) && raw.price !== raw.price_discounted;

  const displayType = getPriceDisplayType(rawProduct);

  // NB: Context for displaying price as "/month" vs "/cycle":
  // There are multiple brand settings that affect how prices are shown:
  // - "lowest_monthly_price" and "abs_min": Both indicate that the price should be displayed as a monthly amount (e.g., "$5/month").
  // - "min": Indicates that the price should be shown for the actual billing cycle (e.g., "$60/year" for a yearly cycle).
  //
  // Historically, we only had two options: show the regular cycle price, or show the lowest monthly price (by dividing the highest term price by its months).
  // However, sometimes the lowest monthly price is not from the longest term, so we introduced "lowest_monthly_term" to calculate the true lowest monthly price.
  //
  // In summary:
  // - Use "/month" display for "lowest_monthly_price" and "abs_min" settings.
  // - Use "/cycle" display for "min" setting.
  // - "lowest_monthly_term" ensures the actual lowest monthly price is shown, regardless of term length.
  // This logic ensures price display is consistent with brand configuration and user expectations.
  const useMonthlyFromPrice =
    (cycle?.months ?? 0) > 1 && displayType !== PriceDisplayTypes.CYCLE;

  return {
    cycle: raw.billing_cycle_months,
    title: cycle ? useTranslateName(cycle) : useTranslateName(raw),
    name: cycle?.name,
    promotions: parsePromotionDetails(raw),
    meta: {
      oneoff: raw.billing_cycle_months == 0,
      mixed: raw.mixed_promotions,
      discounted,
      includesTax: includesTax.value,
      free: (raw.price_discounted ?? raw.price) == 0,
      overrides: !!overrides,
      useMonthlyFromPrice
    }
  } as ProductSummaryDetailWithPrice;
};

export const parsePrice = (raw: IProductPrice): PriceDetail => {
  //  TODO: currently IProductPrice does not provide nett/gross values, only the brand setting
  const money = useMoney();
  const { uiCart } = useBrand();

  const savingAmount =
    Math.round(subtract(raw.price, raw?.price_discounted ?? raw.price) * 100) /
    100;

  const discounted =
    !isNil(raw.price_discounted) && raw.price !== raw.price_discounted;
  const trimTrailingZeroes =
    uiCart.value?.ui?.product?.display_price?.trim_trailing_zeroes ?? false;

  return {
    currentAmount: raw.price_discounted ?? raw.price,
    currentPrice: money.parsePrice(
      raw.price_discounted_formatted ?? raw.price_formatted,
      { trimTrailingZeroes }
    ),
    regularAmount: raw.price,
    regularPrice: money.parsePrice(raw.price_formatted, { trimTrailingZeroes }),
    savingAmount,
    savingPrice: "", //TODO: missing formatted value
    savingPercent: discounted
      ? `${Math.round((savingAmount / raw.price) * 100)}%`
      : ""
  };
};

export const parseSummaryDetailWithPrice = (
  raw: IProductPrice,
  rawProduct: IProduct,
  overrides?: boolean
): ProductSummaryDetailWithPrice => {
  const result: ProductSummaryDetailWithPrice = parseSummaryDetail(
    raw,
    rawProduct,
    overrides
  );
  result.price = parsePrice(raw);
  return result;
};

export const parsePromotionDetails = (
  raw: IProductPrice
): PromotionDetails[] => {
  //  Promotions can be display in one of 3 ways:
  //  - As a generic summary label with no values, eg "SAVE"
  //  - As a summary percentage, eg "Save 20%"
  //  - As individual names, eg ["20% off", "Black Friday"]
  // NB: we always supply the amounts so we can show meta data if needed, eg a tooltip

  // ---
  const { getConfig } = useBrand();

  const promotionDisplayType: PromotionDisplayTypes = get(
    getConfig(BrandConfigKeys.SHOW_PROMOTION_AS),
    BrandConfigKeys.SHOW_PROMOTION_AS,
    PromotionDisplayTypes.PERCENTAGE
  );

  if (isEmpty(raw?.promotions)) return [];

  // ---

  if (promotionDisplayType == PromotionDisplayTypes.NAME) {
    return map(
      raw.promotions,
      rawPromo =>
        ({
          code: rawPromo.code,
          name: rawPromo.name,
          title: useTranslateName(rawPromo),
          description: useTranslateField(rawPromo, "description"),
          excerpt: useTranslateField(rawPromo, "short_description"),
          meta: {
            display: promotionDisplayType,
            mixed: !!raw.mixed_promotions,
            discounted: !isEmpty(rawPromo.amount)
          },
          price: {
            savingAmount: toNumber(rawPromo.amount),
            savingPrice: rawPromo.amount_formatted,
            savingPercent: "" //TODO: missing % value from response
          }
        }) as PromotionDetails
    );
  } else {
    const saving =
      ((raw.price - (raw.price_discounted ?? raw.price)) / raw.price) * 100;
    const saving_formatted = `${Math.round(saving)}%`;

    return [
      {
        code: map(raw.promotions, "code").toString(),
        name: map(raw.promotions, "name").toString(),
        title: map(raw.promotions, useTranslateName).toString(),
        meta: {
          display: promotionDisplayType,
          mixed: !!raw.mixed_promotions,
          discounted: !isNil(raw.price_discounted) && !raw.mixed_promotions
        },
        price: {
          savingAmount:
            isNil(raw.price_discounted) || raw.mixed_promotions ? 0 : saving,
          savingPrice: "", // TODO: misisng formatted value
          savingPercent:
            isNil(raw.price_discounted) || raw.mixed_promotions
              ? ""
              : saving_formatted
        }
      } as PromotionDetails
    ];
  }
};

export const parseProvisioningSchema = (data: any, product: IProduct) => {
  const { getCountry } = useSystem();

  const defaultCountry = getCountry();

  const schema = useLaravalSchemaParser(data, {
    defaultCountry,
    product
  });

  // TODO: Implement a proper solution for this where field type is input_sld
  // if (field.name === "sld") {
  //   //type = "string";
  //   format = "sld";
  //   // TODO: Set the raw TLD rather, not the product name
  //   field.description = product?.name;
  // "The sld may only contain letters, numbers, and dashes"
  // }

  return schema;
};

export const parseProduct = (
  price: PriceDisplay,
  { model, lookups, error, rawBasketProduct }: Partial<ProductConfigContext>
): Product => {
  // sanity check
  if (isEmpty(model) || isEmpty(lookups) || !lookups.product)
    return {} as Product;

  const term = find(lookups.terms, ["cycle", model.term]);
  price = !isNil(price?.regularPrice)
    ? price
    : term?.price || {
        currentAmount: 0,
        currentPrice: "",
        regularAmount: 0,
        regularPrice: "",
        savingAmount: 0,
        savingPrice: "",
        savingPercent: ""
      };
  // ---
  // TODO: Dont have the necessary data now to calculate this
  // price.unit: term?.price.unit,
  // price.configuration: {
  //   total: values.total,
  //   totalFormatted: values.total_formatted,
  //   subtotal: values.subtotal,
  //   subtotalFormatted: values.subtotal_formatted,
  //   discount: values.discounted,
  //   discountFormatted: values.discounted_formatted,
  // },

  const summaryDetailWithPrice: ProductSummaryDetailWithPrice = {
    name: "totals",
    title: lookups.product?.title ?? "",
    category: lookups.product?.category ?? "",
    cycle: model.term,
    quantity: model.quantity,
    meta: {
      ...(term?.meta ?? {}),
      free: price.currentAmount == 0,
      discounted:
        price.currentAmount != price.regularAmount && price.regularAmount > 0
    },
    promotions: term?.promotions,
    // ---
    price
  };
  // -------
  // this is an array of  key value pairs that can be used to display a summary of the configuration
  // typically used in the basket or checkout
  // it is in this format to preserve the order of the configuration
  // and allow for easy i18n
  const productDetails: ProductSummaryDetail[] = [
    {
      name: "product",
      title: lookups.product?.title,
      category: lookups.product?.category,
      meta: {}
    },
    {
      name: "category",
      title: lookups.product.category,
      meta: {}
    }
  ];

  const termDetails = parseSummaryTerm(
    model.term ?? 0,
    lookups.terms ?? [],
    (error as ExternalError)?.term
  );

  const optionDetails = parseSummarySubproduct(
    "option",
    model.options,
    lookups.options,
    (error as ExternalError)?.options
  );

  const attributeDetail = parseSummarySubproduct(
    "attribute",
    model.attributes,
    lookups.attributes,
    (error as ExternalError)?.attributes
  );

  const provisionFieldDetails = parseSummaryProvisionFields(
    model.provisionFields,
    lookups.provisionFields,
    (error as ExternalError)?.provisionFields
  );

  // ---------------------------------------------------------------------------
  return {
    id: model.id,
    configuration: model,
    productDetails: lookups.product,
    promotions: term?.promotions,
    meta: summaryDetailWithPrice.meta,
    price,
    pricing: [summaryDetailWithPrice],
    details: compact(
      concat(
        productDetails,
        termDetails,
        optionDetails,
        attributeDetail,
        provisionFieldDetails
      )
    ),
    errors: omitBy(error, isEmpty) as ExternalError
  };
};

const parseSummaryTerm = (
  cycle: number,
  terms: TermDetails[],
  error?: ExternalError["term"]
): TermDetails | undefined => {
  const { t } = useI18n();
  const term = find(terms, ["cycle", cycle]);
  if (term) {
    term.name = "term";
    term.category = t("text.billing_cycle");
    term.meta = {
      ...term.meta,
      invalid: has(error, "term")
    };
    return term;
  }

  return undefined;
};

const parseSummarySubproduct = (
  key: string,
  data: ProductModel["options"],
  lookup?: SubproductDetails[],
  error?: ExternalError["options" | "attributes"]
): (ProductSummaryDetail | ProductSummaryDetailWithPrice)[] => {
  return reduce(
    data,
    (result, choices) => {
      if (choices) {
        const selected = reduce(
          choices,
          (result, choice, id) => {
            const category = find(lookup, {
              values: [{ id }]
            }) as SubproductDetails;

            const subproduct = find(category?.values, {
              id
            }) as SubproductValue;

            if (subproduct) {
              const summary:
                | ProductSummaryDetail
                | ProductSummaryDetailWithPrice = {
                name: key,
                quantity: choice.quantity,
                category: category?.title,
                title: subproduct.title,
                cycle: subproduct.cycle,
                // ---
                meta: {
                  ...subproduct.meta,
                  invalid: has(error, `${key}.${id}`)
                },
                // ---
                ...(subproduct.price ?? {})
              };

              result.push(summary);
            }

            return result;
          },
          [] as (ProductSummaryDetail | ProductSummaryDetailWithPrice)[] // Provide initial value as an empty array
        );
        result.push(...selected);
      }
      return result;
    },
    [] as (ProductSummaryDetail | ProductSummaryDetailWithPrice)[] // Provide initial value as an empty array
  );
};

const parseSummaryProvisionFields = (
  data: any,
  schema: any,
  error?: ExternalError["provisionFields"]
): ProductSummaryDetail[] => {
  return reduce(
    schema?.properties,
    (result: any[], provisionField, key) => {
      let title = get(data, key);
      if (provisionField.oneOf) {
        title = find(provisionField.oneOf, ["const", title])?.title ?? title;
      }
      result.push({
        name: `provision_field.${key}`,
        category: get(provisionField, "title", key),
        title,
        cycle: undefined,
        quantity: undefined,
        currentAmount: undefined,
        currentPrice: undefined,
        regularAmount: undefined,
        regularPrice: undefined,
        meta: {
          invalid: some(error, ["data.schemaPath", key])
        }
      });
      return result;
    },
    [] as ProductSummaryDetail[]
  );
};

export const parseModel = (data: ProductModel): ProductModel => {
  // if we have subproducts, we need to handle those first and assign them to options/attributes

  // handle  product model
  return {
    quantity: data?.quantity || 1,
    productId: data.productId,
    term: data.term,
    options: data.options,
    attributes: data.attributes,
    provisionFields: data.provisionFields
  };
};

export const parseProductProps = (
  data: ProductProps,
  raw: IProduct,
  preferredCycle?: number // If we have chosen a term then we need to try use that term
): ProductModel => {
  const { defaultPaymentPeriod } = useBrand();

  const paymentPeriod = preferredCycle ?? defaultPaymentPeriod.value;

  const productDetails = parseProductDetails(raw);
  const terms = parseTermDetails(raw);
  const defaultTerm = calculateBillingTerm(
    paymentPeriod || raw.default_payment_period,
    terms
  );

  // We need to find the  subbproducts id from the product option OR attributes, and then map it to that CategoryID
  const matchedOptions = filter(raw.products_options ?? raw.options, option =>
    data?.subproducts?.includes(option.id)
  ) as IProductOption[];
  const options: SubproductModel =
    parseSubproductDetailsChoices(matchedOptions);

  const matchedAttributes = filter(raw.products_attributes, attribute =>
    data?.subproducts?.includes(attribute.id)
  ) as IProductAttribute[];

  const attributes: SubproductModel =
    parseSubproductDetailsChoices(matchedAttributes);

  return {
    // id: raw.id,
    quantity: parseQuantity(data.quantity, productDetails),
    productId: data.productId,
    term: data?.term ?? defaultTerm.cycle,
    options: merge({}, options, data?.options),
    attributes: merge({}, attributes, data?.attributes),
    provisionFields: data.provisionFields || {}
  };
};

export const parseBasketProductModel = (raw: IBasketProduct): ProductModel => {
  // map basket product raw
  return {
    // id: raw.id,
    quantity: raw.quantity,
    productId: raw.product_id,
    term: raw.billing_cycle_months,
    options: parseBasketSubproductDetailsChoices(raw.options),
    attributes: parseBasketSubproductDetailsChoices(raw.attributes),
    provisionFields: raw.provision_fields
  };
};

const parseSubproductDetailsChoices = (
  values: IProductAttribute[] | IProductOption[]
): SubproductModel => {
  return reduce(
    values,
    (result: SubproductModel, value) => {
      // -- defensive
      if (!value?.category_id || !value.id) {
        return result;
      }

      set(result, [value.category_id, value.id], {
        productId: value.id,
        quantity: parseQuantity(
          value.unit_quantity,
          parseProductDetails(value)
        ),
        cycle: value.billing_cycle_months
      });
      return result;
    },
    {}
  );
};

const parseBasketSubproductDetailsChoices = (values: IBasketProduct[]) => {
  return reduce(
    values,
    (result, value) => {
      // -- defensive
      if (!value?.product?.category_id || !value.product_id) return result;

      set(result, [value.product.category_id, value.product_id], {
        productId: value.product_id,
        quantity: parseQuantity(
          value.unit_quantity,
          parseProductDetails(value.product)
        ),
        cycle: value.billing_cycle_months
      });
      return result;
    },
    {}
  );
};

// -----------------------------------------------------------------------------

/**
 * Parses the given product and returns a list of bundled products.
 * The bundled products are extracted from the product, and only the single products are considered.
 * Inactive bundled products are not included.
 * NB: Bundles have a priority...
 *     if a product has bundles defined in its meta, those are used.
 *     otherwise we traverse the category hierarchy to find bundles and take the first set we find.
 * NB: Bundles may be an array or an Collection of key/value pairs.
 *     If we have an array, we use it directly, no key is needed.
 *     However, if we have a key/value object, we need to extract the bundle config based on the provided `bundle` key.
 *     if no key is provided, we will NOT include any bundles.
 *
 * @param {IProduct} raw - The raw product data to parse.
 * @param {ProductConfigContext["bundle"]} bundle - Optional key to extract specific bundle configurations from the product's meta.
 * @returns {ProductProps[]} The parsed list of bundled products configurations.
 */
export function parseBundledProducts(
  raw: IProduct,
  bundle?: ProductConfigContext["bundle"]
): ProductProps[] {
  // safe check: don't include recommendations for products that are not single products
  if (raw?.product_type !== ProductTypes.SINGLE_PRODUCT) return [];
  let bundles: ProductBundles =
    raw?.meta?.bundle ??
    first(
      compact(
        iterateParents(raw.category, [], {
          valueKey: "meta.bundle",
          parentKey: "top_category",
          transform: (category: IProductCategory) =>
            get(category, "meta.bundle")
        })
      )
    );
  if (!isArray(bundles)) {
    if (!bundle) bundles = [];
    else bundles = get(bundles, bundle, []) as ProductBundle[];
  }

  return reduce(
    bundles,
    (result: ProductProps[], rawBundle) => {
      const model = parseBundleConfig(rawBundle);
      if (model) result.push(model);
      return result;
    },
    []
  ) as ProductProps[];
}

function parseBundleConfig(raw: ProductBundle): ProductProps | undefined {
  // safe check : dont include recommendations for products that are not single products

  const valid = raw?.object_type === "product" && raw?.active;
  if (!valid) return undefined; // skip if not a valid product bundle or inactive

  const config: IProductConfig = get(raw, "config", {});

  return {
    productId: raw.object_id,
    quantity: config?.qty || 1,
    term: config?.bcm ?? 0,
    subproducts: compact(config?.sub_pids?.toString()?.split(",") ?? []),
    provisionFields: config?.pfields ?? {},
    coupons: compact(config?.coupons?.toString()?.split(",") ?? []),
    // ---
    silent: true // always silent for bundled products
  } as ProductProps;
}

export const parseProductImages = (images: IImage[]): ProductImage[] => {
  return map(images, image => ({
    url: useImageUrl(image.full_url, "400x400"),
    default: !!image.default
  })) as ProductImage[];
};

/**
 * Maps a billing cycle duration in months to various descriptive formats.
 *
 * @param months - The duration of the billing cycle in months.
 * @returns An object with multiple representations of the billing cycle
 */

export function parseBillingCycle(months: number) {
  const years = months / 12;
  const { t } = useI18n();

  switch (months) {
    case 0:
      return {
        adverbial: t("term.once"), // Once
        descriptive: t("term.one_time"), // One time
        monthly: t("term.one_time"), // One time
        suffix: "", //
        numeric: t("term.one_time") // One time
      };
    case 1:
      return {
        adverbial: t("term.monthly"), // Monthly
        descriptive: t("term.n_months", months), // month
        monthly: t("term.n_months", months), // month
        suffix: t("term.n_mo", months), // mo
        numeric: t("term.n_month", { n: months.toString() }) // 1-month
      };
    case 3:
      return {
        adverbial: t("term.quarterly"), // Quarterly
        descriptive: t("term.n_months", months), // 3 months
        monthly: t("term.n_months", months), // 3 months
        suffix: t("term.n_mo", months), // 3mo
        numeric: t("term.n_month", { n: months.toString() }) // 3-month
      };
    case 6:
      return {
        adverbial: t("term.semiannually"), // Semiannually
        descriptive: t("term.n_months", months), // 6 months
        monthly: t("term.n_months", months), // 6 months
        suffix: t("term.n_mo", months), // 6mo
        numeric: t("term.n_month", { n: months.toString() }) // 6-month
      };
    case 12:
      return {
        adverbial: t("term.annually"), // Annually
        descriptive: t("term.n_years", years), // year
        monthly: t("term.n_months", months), // 12 months
        suffix: t("term.n_yr", years), // yr
        numeric: t("term.n_year", { n: years.toString() }) // 1-year
      };
    case 24:
      return {
        adverbial: t("term.biennially"), // Biennially
        descriptive: t("term.n_years", years), // 2 years
        monthly: t("term.n_months", months), // 24 months
        suffix: t("term.n_yr", years), // 2yr
        numeric: t("term.n_year", { n: years.toString() }) // 2-year
      };
    case 36:
      return {
        adverbial: t("term.triennially"), // Triennially
        descriptive: t("term.n_years", years), // 3 years
        monthly: t("term.n_months", months), // 36 months
        suffix: t("term.n_yr", years), // 3yr
        numeric: t("term.n_year", { n: years.toString() }) // 3-year
      };
    case 48:
    case 60:
    case 72:
    case 84:
    case 96:
    case 108:
    case 120:
      return {
        adverbial: t("term.n_years", years), // {n} years
        descriptive: t("term.n_years", years), // {n} years
        monthly: t("term.n_months", months), // {n} months
        suffix: t("term.n_yr", years), // {n}yr
        numeric: t("term.n_year", { n: years.toString() }) // {n}-year
      };
    default:
      return {
        adverbial: t("term.n_months", months), // {n} months
        descriptive: t("term.n_months", months), // {n} months
        monthly: t("term.n_months", months), // {n} months
        suffix: t("term.n_mo", months), // {n}mo
        numeric: t("term.n_month", { n: months.toString() }) // {n}-month
      };
  }
}
